import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import productConfig from 'Models/uiConfig/productConfig';
import type { ProductImageCrop } from 'Models/uiConfig/productConfig.types';
import type { ServerModel, ServerValue } from 'Modules/serverApi/types';
import defaultRequestConfig from 'Platforms/bigcommerce/requestConfig';
import { createOnImageError, setImageSize } from 'Utils/components';

const {
  searchResultsImageWidth,
  searchResultsImageHeight,
  crop: defaultCrop,
  dropdownImageWidth,
  dropdownImageHeight,
  noImageSrc,
  fields,
  entityIdField,
} = productConfig;

const platformRoute = defaultRequestConfig?.platformRoute ?? null;

export type SearchItemRawData = ServerModel.SearchItem;

// Search items must have an `id`.
export function isSearchItemRawData(value: unknown): value is SearchItemRawData {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const { id } = value as Record<string, unknown>;
  return typeof id === 'string' || typeof id === 'number';
}

type SearchItemImageResizeOptions = {
  width?: number;
  height?: number;
  crop?: ProductImageCrop;
};

function createTemplateHelpers(type: string, onImageError: ReturnType<typeof createOnImageError>) {
  return {
    formatPrice: productConfig.formatPrice,
    calcDiscount,
    getVerifyFitmentOrderNotes,
    resizeImage: createImageResizer(type),
    setImageSize,
    onImageError,
    removeHTML,
    breakWordOnSlash,
    imageOrDefault,
    toKebabCase,
  };
}

export type SearchItemTemplateHelpers = ReturnType<typeof createTemplateHelpers>;

export type SearchItemBaseTemplateParams = SearchItemRawData & SearchItemTemplateHelpers;

export type SearchItemTemplateParams<TExtra extends object = Record<never, never>> =
  SearchItemBaseTemplateParams & TExtra;

export default class SearchItemData {
  static getFromRaw(item: SearchItemRawData, key: string): ServerValue {
    return get({ _item: item }, key);
  }

  _item: SearchItemRawData;
  type: string;
  position: number;
  onImageError: ReturnType<typeof createOnImageError>;

  constructor(item: SearchItemRawData, type: string, position: number) {
    const itemUrl = item.url;

    this._item = {
      ...item,
      url:
        typeof itemUrl === 'string' && platformRoute && !itemUrl.startsWith(platformRoute)
          ? `${platformRoute}${itemUrl}`
          : itemUrl,
    };
    this.type = type;
    this.position = position;
    this.onImageError = createOnImageError();
  }

  get(field: string): ServerValue {
    return get(this, field);
  }

  get id(): ServerValue {
    return get(this, 'id');
  }

  get entityId(): ServerValue {
    const entityId = get(this, entityIdField);

    // Some platforms expose entity IDs as arrays.
    return Array.isArray(entityId) ? entityId[0] : entityId;
  }

  get key(): string | undefined {
    const key = get(this, 'key');
    return typeof key === 'undefined' ? undefined : String(key);
  }

  get price(): ServerValue {
    return get(this, 'price');
  }

  get url(): string | undefined {
    const url = get(this, 'url');
    return typeof url === 'string' ? url : undefined;
  }

  get params(): SearchItemTemplateParams {
    return {
      ...this._item,
      ...createTemplateHelpers(this.type, this.onImageError),
    };
  }

  get plainObject(): SearchItemRawData {
    return this._item;
  }
}

const imageResizerCache: Record<
  string,
  (image: string | null | undefined, opts?: SearchItemImageResizeOptions) => string | null
> = {};

function createImageResizer(type: string) {
  const key = ['autocomplete', 'history'].includes(type) ? 'dropdown' : 'normal';
  if (!imageResizerCache[key]) {
    const [defaultWidth, defaultHeight] =
      key === 'dropdown'
        ? [dropdownImageWidth, dropdownImageHeight]
        : [searchResultsImageWidth, searchResultsImageHeight];
    imageResizerCache[key] = (
      image,
      { width = defaultWidth, height = defaultHeight, crop = defaultCrop } = {},
    ) => setImageSize(image ?? null, width, height, crop);
  }
  return imageResizerCache[key];
}

function removeHTML(value: string | string[] | null | undefined): string | undefined {
  const valueToReplace = Array.isArray(value) ? (value.length ? value[0] : '') : value;
  return valueToReplace?.replace(/<\/?b>/g, '');
}

function breakWordOnSlash(value: string | string[] | null | undefined): string | undefined {
  const valueToReplace = Array.isArray(value) ? (value.length ? value[0] : '') : value;
  return valueToReplace?.replace(/(\w\/)(\w)/g, '$1​$2');
}

function calcDiscount(price: number, compareAtPrice: number): number {
  return compareAtPrice > 0 && compareAtPrice > price
    ? Math.floor(
        Number(((Math.round(compareAtPrice * 100) - Math.round(price * 100)) / compareAtPrice).toFixed(2)),
      )
    : 0;
}

function getVerifyFitmentOrderNotes(fits: string): string | undefined {
  return fitmentSearchConfig.getVerifyFitmentOrderNotes(fits);
}

function imageOrDefault(image: string | null | undefined): string {
  return image || noImageSrc;
}

function get({ _item }: { _item: SearchItemRawData }, key: string): ServerValue {
  const fieldName = getFieldName(key);
  return fieldName ? _item[fieldName] : undefined;
}

function getFieldName(key: string): string | undefined {
  const fieldName = fields[key];
  return typeof fieldName === 'string' ? fieldName : undefined;
}

function toKebabCase(string: string | null | undefined): string | undefined {
  return string?.toLowerCase()?.replace(' ', '-');
}
